import { useEffect, useRef } from 'react';
import { useConversationStore } from '@/stores/conversationStore';
import { useActivityStore } from '@/stores/activityStore';
import { getConversationLabel } from '@/utils/conversationLabel';
import { BRAND, STATUS } from '@/theme/tokens';
import { customerAskedForPerson } from '@/utils/consultationReason';
import type { SessionState } from '@/types';

interface Prev {
  state: SessionState;
  label: string;
  /** 2026-10-28 - the bot is waiting for the team because the customer asked for a person. */
  asked: boolean;
}

/**
 * A row that appears in the store counts as a NEW conversation only if it was created this recently. Rows now also
 * arrive by paging (infinite scroll, a folder switch, a reconnect reload), and every one of those would otherwise read
 * as "New conversation from X entered". A conversation that really just arrived (the live SessionCreated push) was
 * created seconds ago; one that merely came into memory was created earlier.
 */
export const NEW_CONVERSATION_WINDOW_MS = 2 * 60 * 1000;

const isRecent = (createdAt: string | undefined, now: number): boolean => {
  const at = Date.parse(createdAt ?? '');
  return Number.isFinite(at) && now - at >= -NEW_CONVERSATION_WINDOW_MS && now - at <= NEW_CONVERSATION_WINDOW_MS;
};

/**
 * Derives the My Day "live activity" feed from real conversation-store
 * transitions (which are driven by hub events) — no fabrication. Mounted once
 * in the shell so the feed accrues while the agent works, regardless of which
 * view is open. Skips the initial hydration so the feed doesn't flood with one
 * "new" entry per already-open conversation on first load.
 *
 * ADR-033: a row VANISHING (hidden because someone else claimed it, a folder reset, cache eviction) emits nothing - only a
 * real transition to CLOSED is "resolved" - and a row merely APPEARING by paging is not "new" (see
 * NEW_CONVERSATION_WINDOW_MS). A conversation that is hidden from this viewer never produces a line, and each line
 * carries its `sessionId` so the My Day feed can drop it if the conversation is hidden afterwards.
 */
export function useActivityFeed(): void {
  const conversations = useConversationStore((s) => s.conversations);
  const push = useActivityStore((s) => s.push);
  const prevRef = useRef<Map<string, Prev> | null>(null);
  // Conversations already announced as new: a row that leaves memory and comes back (a folder reset) is not new twice.
  const announcedNewRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const current = new Map<string, Prev>();
    for (const c of conversations) {
      current.set(c.sessionId, { state: c.state, label: getConversationLabel(c), asked: customerAskedForPerson(c) });
    }

    // First run: seed the baseline without emitting activity.
    if (prevRef.current === null) {
      prevRef.current = current;
      return;
    }

    const prev = prevRef.current;
    const now = () => new Date().toISOString();
    const stamp = Date.now();
    const gate = useConversationStore.getState();
    const blocked = (id: string) => id in gate.hiddenSessions || !!gate.spamTombstones[id];
    const createdAt = new Map(conversations.map((c) => [c.sessionId, c.createdAt]));

    // New conversations + state transitions.
    for (const [id, cur] of current) {
      if (blocked(id)) continue;
      const before = prev.get(id);
      if (!before) {
        if (!announcedNewRef.current.has(id) && isRecent(createdAt.get(id), stamp)) {
          announcedNewRef.current.add(id);
          push({
            id: `new-${id}-${stamp}`,
            icon: 'queue',
            color: BRAND.blue,
            textKey: 'act_new',
            name: cur.label,
            sessionId: id,
            at: now(),
          });
        }
        continue;
      }
      // 2026-10-28 (operator) - the URGENT variant of "the bot requested help": the customer asked for a person. Its own
      // line in the danger colour, emitted when the row starts asking (the reason can land a moment after the state).
      if (cur.asked && !before.asked) {
        push({ id: `human-${id}-${stamp}`, icon: 'hand', color: STATUS.danger, textKey: 'act_human_requested', name: cur.label, sessionId: id, at: now() });
      }
      if (before.state !== cur.state) {
        if (cur.state === 'CLOSED') {
          // OP-373 keeps a closed row in the store, so "resolved" is a STATE CHANGE, not a row going missing.
          push({ id: `resolved-${id}-${stamp}`, icon: 'check', color: STATUS.success, textKey: 'act_resolved', name: cur.label, sessionId: id, at: now() });
        } else if (cur.state === 'CONSULTATION_REQUESTED') {
          push({ id: `consult-${id}-${stamp}`, icon: 'sparkle', color: STATUS.consult, textKey: 'act_consult', name: cur.label, sessionId: id, at: now() });
        } else if (cur.state === 'HUMAN_TAKEOVER') {
          push({ id: `takeover-${id}-${stamp}`, icon: 'takeover', color: BRAND.blue, textKey: 'act_takeover', name: cur.label, sessionId: id, at: now() });
        }
      }
    }

    // A row that is simply GONE (hidden because it is no longer mine, a folder reload, cache eviction) emits nothing:
    // disappearing is not an outcome. Only a real transition to CLOSED above is "resolved".

    prevRef.current = current;
  }, [conversations, push]);
}
